import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Icon from '@cloudscape-design/components/icon';
import Input from '@cloudscape-design/components/input';
import { useState } from 'react';
import type { Email, Ticket } from '../data/mockEmails';
import { formatRelativeTime } from '../utils/dateUtils';

interface TicketPanelProps {
  email: Email | null;
  ticket: Ticket | undefined;
}

function StatusBadge({ status }: { status: Ticket['status'] }) {
  const colorMap: Record<Ticket['status'], 'blue' | 'red' | 'green' | 'grey'> = {
    Open: 'blue',
    'In Progress': 'red',
    Resolved: 'green',
    Closed: 'grey',
  };
  return <Badge color={colorMap[status]}>{status}</Badge>;
}

export default function TicketPanel({ email, ticket }: TicketPanelProps) {
  const [noteText, setNoteText] = useState('');

  if (!email) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '24px',
          color: '#6b7280',
        }}
      >
        <Icon name="ticket" size="big" />
        <Box margin={{ top: 's' }} color="text-body-secondary" textAlign="center">
          Select an email to see ticket info
        </Box>
      </div>
    );
  }

  if (ticket) {
    return (
      <div style={{ padding: '16px' }}>
        <SpaceBetween size="l">
          <Container
            header={
              <Header
                variant="h3"
                description="This thread is linked to a ticket"
                info={<Icon name="status-positive" variant="success" />}
              >
                Linked Ticket
              </Header>
            }
          >
            <SpaceBetween size="m">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box fontWeight="bold" fontSize="heading-m">
                  {ticket.ticketId}
                </Box>
                <StatusBadge status={ticket.status} />
              </div>
              <Box color="text-body-secondary">{ticket.title}</Box>
              <div>
                <Box variant="awsui-key-label">Assignee</Box>
                <Box>{ticket.assignee}@</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Last Updated</Box>
                <Box>{formatRelativeTime(ticket.lastUpdated)}</Box>
              </div>
            </SpaceBetween>
          </Container>

          {/* Timeline */}
          <Container header={<Header variant="h3">Activity</Header>}>
            <SpaceBetween size="xs">
              {ticket.timeline.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '8px',
                    fontSize: '13px',
                    padding: '6px 0',
                    borderBottom: i < ticket.timeline.length - 1 ? '1px solid #f0f0f0' : undefined,
                  }}
                >
                  <div style={{ color: '#6b7280', whiteSpace: 'nowrap', fontSize: '12px' }}>
                    {formatRelativeTime(entry.timestamp)}
                  </div>
                  <div>{entry.event}</div>
                </div>
              ))}
            </SpaceBetween>
          </Container>

          {/* Actions */}
          <SpaceBetween size="s">
            <Button variant="primary" fullWidth>
              View Full Ticket →
            </Button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                value={noteText}
                onChange={({ detail }) => setNoteText(detail.value)}
                placeholder="Add a note..."
              />
              <Button iconName="send" variant="icon" ariaLabel="Send note" />
            </div>
          </SpaceBetween>
        </SpaceBetween>
      </div>
    );
  }

  // No linked ticket — show "Create Ticket" flow
  return (
    <div style={{ padding: '16px' }}>
      <SpaceBetween size="l">
        <Container
          header={
            <Header variant="h3" description="Create a ticket to track this conversation">
              No Linked Ticket
            </Header>
          }
        >
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Title</Box>
              <Box>{email.subject}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Requester</Box>
              <Box>
                {email.fromName} ({email.from})
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Description</Box>
              <Box color="text-body-secondary">{email.snippet.slice(0, 200)}</Box>
            </div>
          </SpaceBetween>
        </Container>
        <SpaceBetween size="xs">
          <Button variant="primary" fullWidth>
            Create Ticket
          </Button>
          <Button variant="normal" fullWidth>
            Link to Existing…
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </div>
  );
}
